import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, Wifi, Download, Upload, Clock } from 'lucide-react';
import { useDarkMode } from '../../darkmode/DarkModeContext';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  'http://localhost:5000';

// Fungsi untuk mengarahkan ke WhatsApp dengan pesan khusus Fiber Optic
const handleWhatsAppClick = (pkg) => {
  const packageName = pkg.name;
  const speed = pkg.specifications?.speed || pkg.speed || '-';
  const bandwidth = pkg.specifications?.bandwidth || '-';
  const latency = pkg.specifications?.latency || '-';
  const mainFeatures = pkg.features?.slice(0, 3).join(', ') || '-';
  
  const message = `Halo, saya tertarik dengan *Paket Fiber Optic ${packageName}* 

📋 *Detail Paket:*
• Nama Paket: ${packageName}
• Kecepatan: ${speed}
• Bandwidth: ${bandwidth}
• Latency: ${latency}
• Fitur Utama: ${mainFeatures}

💬 Saya ingin konsultasi mengenai:
- Harga paket lengkap
- Spesifikasi teknis detail
- Cakupan area layanan
- Proses instalasi
- SLA (Service Level Agreement)
- Garansi dan maintenance

Mohon info lebih lanjut. Terima kasih!`;

  const phoneNumber = '6287778882022';
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
};

const Diskon = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedPackage, setSelectedPackage] = useState(null);

  // State untuk data dari database
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const productTabs = [
    { id: 'all', name: 'Semua Paket', icon: <Wifi className="w-4 h-4" /> },
  ];

  // Fetch paket dari database berdasarkan kategori Fiber Optic
  useEffect(() => { 
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await fetch(`${API_BASE}/api/packages?category=fiber_optic`, { signal: ac.signal });
        const json = await res.json();
        if (!json?.success) throw new Error(json?.message || 'Gagal mengambil paket');
        const fiberOpticPackages = Array.isArray(json.data) 
          ? json.data.filter(pkg => pkg.category === 'fiber_optic') 
          : [];
        setPackages(fiberOpticPackages);
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message || 'Terjadi kesalahan jaringan');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const currentPackages = useMemo(() => {
    return packages.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
  }, [packages]);

  const handlePackageSelect = (pkg) => setSelectedPackage(pkg);
  const closePackageDetail = () => setSelectedPackage(null);

  return (
    <div
      className="min-h-screen bg-cover bg-center transition-colors duration-300"
      style={{
        backgroundColor: isDarkMode ? '#111827' : 'white',
        backgroundBlendMode: 'overlay',
      }}
    >
      {/* Scrollbar styles */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkMode ? '#374151' : '#e5e7eb'};
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkMode ? '#1E9B4C' : '#10b981'};
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkMode ? '#15803d' : '#059669'};
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: ${isDarkMode ? '#1E9B4C #374151' : '#10b981 #e5e7eb'};
          }
          .smooth-scroll {
            scroll-behavior: smooth;
          }
        `}
      </style>

      {/* Packages Section */}
      <section className="py-8 sm:py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex justify-center mb-4">
              <Wifi className="w-12 h-12 text-green-500" />
            </div>
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 transition-colors ${
              isDarkMode ? 'text-green-50' : 'text-gray-900'
            }`}>
              Paket Fiber Optic
            </h2>
            <p className={`text-base sm:text-lg transition-colors max-w-2xl mx-auto ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Koneksi internet berkecepatan tinggi dengan teknologi fiber optic terbaru untuk kebutuhan bisnis dan rumah
            </p>
          </div>

          {loading && (
            <div className={`text-center transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Memuat paket…
            </div>
          )}

          {err && (
            <div className="text-center py-12 text-red-500">
              <p>Terjadi kesalahan: {err}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Coba Lagi
              </button>
            </div>
          )}

          {!loading && !err && currentPackages.length === 0 && (
            <div className="text-center py-12 text-gray-600 dark:text-gray-300">
              <p>Tidak ada paket yang tersedia untuk kategori ini.</p>
              <p className="text-sm mt-2">Endpoint: {API_BASE}/api/packages?category=fiber_optic</p>
              <p className="text-sm">Jumlah paket yang diambil: {packages.length}</p>
            </div>
          )}

          {!loading && !err && currentPackages.length > 0 && (
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl">
                {currentPackages.map((pkg) => {
                  return (
                    <div
                      key={pkg._id}
                      className={`relative rounded-2xl p-6 sm:p-8 shadow-lg transition-all duration-300 hover:scale-105 ${
                        pkg.popular
                          ? `bg-gradient-to-br ${pkg.color || 'from-green-500 to-green-700'} text-white shadow-2xl transform scale-105`
                          : isDarkMode
                            ? 'bg-gray-800 text-green-50 border border-gray-700'
                            : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      {/* Badge kategori */}
                      <div className="absolute top-4 left-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          pkg.popular
                            ? 'bg-white text-gray-900'
                            : isDarkMode
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-gray-200 text-gray-700'
                        }`}>
                          FIBER OPTIC
                        </span>
                      </div>

                      {pkg.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-yellow-400 text-gray-900 px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-bold">
                            Recommended
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <div className="flex justify-center mb-3">
                          <Wifi className={`w-8 h-8 ${pkg.popular ? 'text-white' : 'text-green-500'}`} />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold mb-2">{pkg.name}</h3>
                        <div className="mb-4">
                          <span className="text-lg sm:text-xl font-semibold">Konsultasi Gratis</span>
                        </div>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-center justify-between mb-3 text-sm sm:text-base">
                          <span className="flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Download Speed
                          </span>
                          <span className="font-bold text-right">{pkg.specifications?.downloadSpeed || pkg.speed || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between mb-3 text-sm sm:text-base">
                          <span className="flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Upload Speed
                          </span>
                          <span className="font-bold text-right">{pkg.specifications?.uploadSpeed || pkg.upload || '-'}</span>
                        </div>
                        <div className="flex items-center justify-between mb-4 text-sm sm:text-base">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Latency
                          </span>
                          <span className="font-bold text-right">{pkg.specifications?.latency || '-'}</span>
                        </div>

                        {/* Fitur Utama - Untuk paket popular, background hijau */}
                        <div className={`p-3 rounded-lg mb-4 ${
                          pkg.popular 
                            ? 'bg-green text-green-900'
                            : isDarkMode 
                              ? 'bg-gray-700' 
                              : 'bg-gray-50'
                        }`}>
                          <h4 className={`font-semibold text-sm mb-2 text-center ${
                            pkg.popular ? 'text-white' : ''
                          }`}>
                            Fitur Utama
                          </h4>
                          <ul className="space-y-2 max-h-32 overflow-y-auto text-xs">
                            {(pkg.features || []).slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle className={`w-3 h-3 flex-shrink-0 mt-0.5 ${
                                  pkg.popular ? 'text-green-900' : 'text-green-500'
                                }`} />
                                <span className={`text-left ${pkg.popular ? 'text-white' : ''}`}>
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={() => handlePackageSelect(pkg)}
                          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 text-sm border ${
                            pkg.popular
                              ? 'border-white text-white hover:bg-white hover:text-green-700'
                              : isDarkMode
                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                : 'border-green-500 text-green-600 hover:bg-green-50'
                          }`}
                        >
                          Lihat Detail Lengkap
                        </button>
                        
                        <button
                          onClick={() => handleWhatsAppClick(pkg)}
                          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 text-sm ${
                            pkg.popular
                              ? 'bg-white text-green-700 hover:bg-gray-100'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          Konsultasi Sekarang
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Package Detail Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[2001]">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
            isDarkMode ? 'bg-gray-800 text-green-50' : 'bg-white text-gray-900'
          }`}>
            <div className={`sticky top-0 flex justify-between items-center p-4 sm:p-6 border-b ${
              isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center gap-3">
                <Wifi className="w-6 h-6 text-green-500" />
                <h3 className="text-xl sm:text-2xl font-bold">Detail Paket {selectedPackage.name}</h3>
              </div>
              <button 
                onClick={closePackageDetail} 
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Overview */}
                <div>
                  <div className={`p-4 sm:p-6 rounded-xl mb-6 bg-gradient-to-br ${
                    selectedPackage.color || 'from-green-500 to-green-700'
                  } text-white`}>
                    <h4 className="text-lg sm:text-xl font-bold mb-2">{selectedPackage.name}</h4>
                    <p className="opacity-90 text-sm sm:text-base mb-4">{selectedPackage.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">Konsultasi Gratis</span>
                      <Wifi className="w-6 h-6" />
                    </div>
                  </div>

                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Wifi className="w-5 h-5 text-green-500" />
                      <span className="text-sm sm:text-base">Spesifikasi Teknis</span>
                    </h5>
                    <div className="space-y-4">
                      <div>
                        <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Download Speed</span>
                        <span className="font-bold text-green-500 text-lg">{selectedPackage.specifications?.downloadSpeed || selectedPackage.speed || '-'}</span>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Upload Speed</span>
                        <span className="font-bold text-green-500 text-lg">{selectedPackage.specifications?.uploadSpeed || selectedPackage.upload || '-'}</span>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Latency</span>
                        <span className="font-bold text-green-500 text-lg">{selectedPackage.specifications?.latency || '-'}</span>
                      </div>
                      <div>
                        <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Bandwidth</span>
                        <span className="font-bold text-green-500 text-lg">{selectedPackage.specifications?.bandwidth || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features & Specs */}
                <div className="space-y-6">
                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm sm:text-base">Fitur Lengkap</span>
                    </h5>
                    <ul className="space-y-3 max-h-60 overflow-y-auto">
                      {(selectedPackage.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-green-500" />
                      <span className="text-sm sm:text-base">Layanan Tambahan</span>
                    </h5>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span>SLA (Service Level)</span>
                        <span className="font-bold text-green-500">
                          {selectedPackage.features?.find(f => f.includes('SLA')) || '99.9%'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>24/7 Support</span>
                        <span className="font-bold text-green-500">
                          {selectedPackage.features?.find(f => f.includes('Support')) ? 'Termasuk' : 'Standar'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Maintenance</span>
                        <span className="font-bold text-green-500">
                          {selectedPackage.features?.find(f => f.includes('Maintenance')) ? 'Termasuk' : 'Opsional'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Garansi</span>
                        <span className="font-bold text-green-500">
                          {selectedPackage.features?.find(f => f.includes('Garansi')) || '1 Tahun'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleWhatsAppClick(selectedPackage)}
                  className="flex-1 bg-green-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-600 transition-colors text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  Konsultasi via WhatsApp
                </button>
                <button 
                  onClick={closePackageDetail}
                  className={`px-6 py-3 rounded-lg font-semibold border transition-colors text-sm sm:text-base ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Tutup Detail
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Diskon;